/**
 * GoalProgressCard — Dashboard countdown card.
 *
 * "MCAT in 47 days — let's drill Linear Algebra (76→90)."
 *
 * Renders only when the user has said something like "preparing for X this June"
 * in chat (Tutor Memory captures it as a goal) and the date can be extracted.
 * Otherwise returns null so the Dashboard layout collapses naturally.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, ArrowRight } from 'lucide-react';
import api, { apiUrl } from '../utils/apiClient';

export default function GoalProgressCard() {
  const [card, setCard] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(apiUrl('/goal-progress'));
        setCard(data?.card || null);
      } catch { /* silent */ }
    })();
  }, []);

  if (!card) return null;
  const { summary, days_left, weakest } = card;
  const overdue = days_left < 0;
  const urgency =
    overdue ? `${Math.abs(days_left)} days past` :
    days_left === 0 ? 'today!' :
    days_left === 1 ? 'tomorrow' :
    `in ${days_left} days`;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="relative overflow-hidden mb-6 bg-[var(--isp-glass)] border border-[var(--isp-glass-border)]"
      data-testid="goal-progress-card"
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${overdue ? 'bg-red-500' : 'bg-[#002FA7]'}`} />
      <div className="relative px-6 py-6 sm:px-10 sm:py-7">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-3.5 h-3.5 text-[var(--isp-muted)]" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--isp-muted)]">
            Your Goal
          </span>
        </div>
        <h3 className="text-lg sm:text-xl font-bold text-[var(--isp-heading)] tracking-tight leading-snug" data-testid="goal-summary">
          {summary}
        </h3>
        <p className={`mt-1 text-sm font-semibold ${overdue ? 'text-red-500' : 'text-[#002FA7]'}`} data-testid="goal-countdown">
          {urgency}
        </p>
        {weakest?.subject_name && (
          <p className="mt-2 text-sm text-[var(--isp-body)]">
            Weakest tracked subject: <strong className="text-[var(--isp-heading)]">{weakest.subject_name}</strong>
            {' '}({weakest.current_grade}% → {weakest.target_grade}%, {weakest.gap}-point gap)
          </p>
        )}
        <Link
          to={weakest?.subject_id ? `/study-buddy?subject_id=${weakest.subject_id}` : '/study-buddy'}
          className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold text-xs tracking-tight transition-colors"
          data-testid="goal-cta"
        >
          Open AI Tutor with this context
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </motion.div>
  );
}
